/**
 * Pure helpers for GuestLinkControl — kept separate so the URL/expiry math is
 * unit-testable without mounting React or a Supabase client.
 */

export type GuestLinkExpiryChoice = 'none' | '24h' | '7d';

export const GUEST_LINK_EXPIRY_CHOICES: { value: GuestLinkExpiryChoice; label: string }[] = [
  { value: 'none', label: 'No expiry' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
];

/** The guest join URL a facilitator shares — origin + the account-less join route. */
export function buildGuestJoinUrl(origin: string, token: string): string {
  return `${origin}/apps/session/join/${token}`;
}

/** Expiry choice → ISO timestamp to store in `guest_token_expires_at`, or null for no expiry. */
export function computeGuestTokenExpiresAt(choice: GuestLinkExpiryChoice, now: Date): string | null {
  if (choice === 'none') return null;
  const ms = choice === '24h' ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
  return new Date(now.getTime() + ms).toISOString();
}

export interface GuestLinkStatusInput {
  guestAccessEnabled: boolean;
  guestTokenRevokedAt: string | null;
  guestTokenExpiresAt: string | null;
  now: Date;
}

export type GuestLinkStatus = 'off' | 'revoked' | 'expired' | 'active';

/** What the facilitator sees the link's current state as — one source of truth for copy + gating. */
export function deriveGuestLinkStatus(input: GuestLinkStatusInput): GuestLinkStatus {
  if (!input.guestAccessEnabled) return 'off';
  if (input.guestTokenRevokedAt) return 'revoked';
  if (input.guestTokenExpiresAt && new Date(input.guestTokenExpiresAt).getTime() < input.now.getTime()) {
    return 'expired';
  }
  return 'active';
}

/**
 * May the signed-in person turn the guest link on? Mirrors
 * `enable_session_guest_link` (migration 960): the session's facilitator or its
 * creator, compared as text the way the function compares `auth.uid()::text`.
 */
export function guestLinkEnableAllowed(input: {
  userId: string | null | undefined;
  facilitatorId: string | null | undefined;
  createdBy: string | null | undefined;
}): boolean {
  if (!input.userId) return false;
  return input.userId === input.facilitatorId || input.userId === input.createdBy;
}

/** Drawn in place of Turn on / Re-enable for everyone else (build contract §2.8). */
export const GUEST_LINK_ENABLE_LINE =
  "Turning on the guest link takes the session's facilitator or the person who created it.";
